import { faBicycle } from '@fortawesome/pro-regular-svg-icons/faBicycle';
import { faBus } from '@fortawesome/pro-regular-svg-icons/faBus';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faClockEightThirty } from '@fortawesome/pro-regular-svg-icons/faClockEightThirty';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faWalking } from '@fortawesome/pro-regular-svg-icons/faWalking';
import { TIME_OPTIONS, arrivalDayOptions, arrivalTimeOptions } from '@propertypal/shared/src/constants/map';
import { getArrivalTime } from '@propertypal/shared/src/reducers/map';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { Coordinate } from '@propertypal/shared/src/types';
import {
  ImportantPlaceRequest,
  ImportantPlaceResponse,
  TravelMode,
} from '@propertypal/shared/src/types/important-places';
import { MarkerType } from '@propertypal/shared/src/types/map';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TagInput from '@propertypal/web-ui/src/inputs/TagInput';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { Label } from '@propertypal/web-ui/src/inputs/TextInput.style';
import { Box } from '@propertypal/web-ui/src/layout';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import { ArrivalRow } from '@propertypal/web-ui/src/search/TravelSearchBox.style';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { Text } from '@propertypal/web-ui/src/typography';
import { useFormik } from 'formik';
import React, { FunctionComponent, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import GoogleMap, { PlaceResult } from '../map/GoogleMap';
import MapControls from '../map/MapControls';
import {
  Alert,
  AlertMessage,
  AlertMessageContainer,
  Form,
  InputContainer,
  MapContainer,
  SearchInput,
  SubmitButton,
} from './ImportantPlacesModal.style.pn';

const TRAVEL_OPTIONS: { icon: any; value: TravelMode }[] = [
  {
    icon: faCarSide,
    value: 'DRIVE',
  },
  {
    icon: faWalking,
    value: 'WALK',
  },
  {
    icon: faBicycle,
    value: 'BICYCLE',
  },
  {
    icon: faBus,
    value: 'TRANSIT',
  },
];

interface Props {
  show: boolean;
  onClose: () => void;
  importantPlaces?: ImportantPlaceResponse[];
  coordinate?: Coordinate;
  onSubmit: (result: ImportantPlaceResponse[]) => void;
  propertyId?: number;
}

interface ImportantPlacesFormProps extends Pick<Props, 'onSubmit' | 'propertyId' | 'importantPlaces'> {
  place: PlaceResult;
}

type FormValues = Omit<ImportantPlaceRequest, 'coordinate' | 'travelTime'> & {
  travelTime: `${number}`;
  arrivalDay: string;
  arrivalTime: string;
};

const validationSchema = (placeNames: string[]) =>
  yup.object().shape({
    travelTime: yup.number().min(1, 'Travel time must be at least 1 minute').required('Please select a Travel Time'),
    name: yup
      .string()
      .required('Please add a Nickname')
      .notOneOf(placeNames, ({ value }) => `${value} is already the Nickname of one of your saved important places`),
    transport: yup.string().required('Please select a Transport type'),
    arrivalDay: yup.string().when('transport', {
      is: (transport: string) => transport === 'DRIVE' || transport === 'TRANSIT',
      then: (schema) => schema.required('An arrival day is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
    arrivalTime: yup.string().when('transport', {
      is: 'TRANSIT',
      then: (schema) => schema.required('An arrival time is required.'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

export const ImportantPlacesForm: React.FC<ImportantPlacesFormProps> = ({
  place,
  onSubmit,
  propertyId,
  importantPlaces,
}) => {
  const theme = useTheme();

  const placeNames = importantPlaces?.map((p) => p.name) || [];

  const name = useMemo(() => {
    const typeMatch = !!placeNames?.find((p) => p === place.type);
    if (typeMatch) {
      const nameMatch = !!placeNames?.find((p) => p === place.name);
      if (nameMatch) return '';
      return place.name || '';
    }
    return place.type;
  }, [placeNames, place.type]);

  const formik = useFormik<FormValues>({
    initialValues: {
      name,
      description: place.address,
      travelTime: '30',
      transport: 'DRIVE',
      arrivalDay: '1',
      arrivalTime: '',
    },
    validationSchema: validationSchema(placeNames),
    onSubmit: async (values) => {
      const arrivalTime = getArrivalTime(values.arrivalDay, values.arrivalTime, values.transport.toLowerCase());

      const data: ImportantPlaceRequest = {
        coordinate: {
          latitude: place.lat,
          longitude: place.lng,
        },
        description: values.description,
        name: values.name,
        transport: values.transport,
        travelTime: parseInt(values.travelTime, 10) * 60,
        arrivalTime: arrivalTime || null,
      };

      try {
        const result = await axios<ImportantPlaceResponse[]>(
          {
            method: 'PUT',
            url: `${API_URL}/important-place`,
            data,
          },
          undefined,
        );

        if (propertyId) {
          const res = await axios<ImportantPlaceResponse[]>(
            {
              method: 'GET',
              url: `${API_URL}/important-place/property/${propertyId}`,
            },
            undefined,
          );

          onSubmit(res.data || result.data);
        } else onSubmit(result.data);
      } catch (e: any) {
        openToast({
          message:
            e?.response?.status === 400
              ? 'Invalid location, please enter a location close to a road for close to results'
              : "Something went wrong. We couldn't create a place. Please try again!",
          type: 'error',
          title: 'Error',
        });
      }
    },
  });

  return (
    <Box data-testid="important-place-select" mt={16}>
      <Text fontSize={15}>
        <b>Address: </b>
        <span data-testid="important-place-address">{place.address}</span>
      </Text>
      <Form onSubmit={formik.handleSubmit} $isSubmitting={formik.isSubmitting}>
        <InputContainer>
          <Label>Nickname for this place</Label>
          <TextInput
            value={formik.values.name}
            onValueChange={formik.handleChange('name')}
            name="name"
            placeholder="Add a nickname e.g. home or work"
            testID="important-place-name-input"
            borderColour={theme.keyline}
            error={formik.touched.name && formik.errors.name}
          />
        </InputContainer>
        <InputContainer>
          <Label>How close would you like to live to this place?</Label>
          <Text>Within:</Text>

          <TagInput
            value={formik.values.travelTime}
            options={TIME_OPTIONS}
            // @ts-ignore
            onOptionChange={(value) => formik.handleChange('travelTime')(value)}
            testID="travelTimeSelect"
          />

          <Text>By:</Text>

          <TagInput
            value={formik.values.transport}
            options={TRAVEL_OPTIONS}
            // @ts-ignore
            onOptionChange={(transport: TravelMode) => {
              formik.handleChange('transport')(transport);
            }}
            testID="transportSelect"
          />
        </InputContainer>
        {!(formik.values.transport === 'WALK' || formik.values.transport === 'BICYCLE') && (
          <InputContainer>
            <Label>Arrival Time?</Label>

            <ArrivalRow
              $dayError={!!(formik.touched.arrivalDay && formik.errors.arrivalDay)}
              $timeError={!!(formik.touched.arrivalTime && formik.errors.arrivalTime)}
              $isDrive={formik.values.transport === 'DRIVE'}
            >
              {formik.values.transport !== 'DRIVE' && (
                <SelectInput
                  inputStyle={{ height: 46 }}
                  icon={faCalendar}
                  ariaLabel="Travel Arrival Day"
                  placeholder="Select a arrival day"
                  id="arrivalDay"
                  options={arrivalDayOptions}
                  value={formik.values.arrivalDay}
                  onChange={(v) => {
                    formik.setFieldTouched('arrivalTime');
                    formik.handleChange('arrivalDay')(v);
                  }}
                  testID="arrivalDaySelect"
                  error={formik.touched.arrivalDay && formik.errors.arrivalDay}
                  alignTop
                />
              )}
              <SelectInput
                inputStyle={{ height: 46 }}
                icon={faClockEightThirty}
                ariaLabel="Travel Arrival Time"
                placeholder="Select a arrival time"
                id="arrivalTime"
                options={arrivalTimeOptions}
                value={formik.values.arrivalTime}
                onChange={(v) => {
                  formik.setFieldTouched('arrivalDay');
                  formik.handleChange('arrivalTime')(v);
                }}
                testID="arrivalTimeSelect"
                error={formik.touched.arrivalTime && formik.errors.arrivalTime}
                alignTop
              />
            </ArrivalRow>
          </InputContainer>
        )}
        <SubmitButton type="submit" loading={formik.isSubmitting} testID="important-places-submit">
          Add Place
        </SubmitButton>
      </Form>
    </Box>
  );
};

const ImportantPlacesModal: FunctionComponent<Props> = ({
  show,
  onClose,
  importantPlaces,
  coordinate,
  onSubmit,
  propertyId,
}) => {
  const theme = useTheme();

  const [search, setSearch] = useState<string>('');
  const [place, setPlace] = useState<PlaceResult | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const markers = useMemo(() => {
    if (place?.lat && place?.lng) {
      const m: MarkerType[] = [
        {
          id: 1,
          lat: place?.lat,
          lng: place?.lng,
          icon: {
            default: {
              src: '/map/important-place-marker.svg',
              width: 32,
              height: 32,
            },
            selected: {
              src: '/map/important-place-marker.svg',
              width: 32,
              height: 32,
            },
          },
        },
      ];

      return m;
    }
    return undefined;
  }, [place]);

  return (
    <WhiteModal title="Add a place" show={show} onClose={onClose} maxWidth={768}>
      <Alert>
        <FontAwesomeIcon icon={faInfoCircle} size={22} />
        <AlertMessageContainer>
          <AlertMessage>
            <span>You can add up to 5 places.</span>
            <Text color="#757575" fontWeight="600" data-testid="total-important-places">
              {importantPlaces?.length || 0}/5 Added
            </Text>
          </AlertMessage>
        </AlertMessageContainer>
      </Alert>
      <SearchInput
        value={search}
        onValueChange={(v) => setSearch(v)}
        name="addressSearch"
        placeholder="Search for your place by name or address"
        testID="address-search-input"
        // @ts-ignore
        inputRef={inputRef}
        icon={faMagnifyingGlass}
        borderColour={theme.keyline}
      />
      <MapContainer>
        <GoogleMap
          width="100%"
          height="100%"
          gestureHandling="greedy"
          inputRef={inputRef}
          hideStreetView
          onPlaceSelect={(p) => {
            setSearch('');
            setPlace(p);
          }}
          lat={coordinate?.latitude}
          lng={coordinate?.longitude}
          markers={markers}
        >
          <MapControls containerStyle={{ top: 10 }} zoomControl mapTypeControl />
        </GoogleMap>
      </MapContainer>
      {!!place && (
        <ImportantPlacesForm
          place={place}
          onSubmit={onSubmit}
          propertyId={propertyId}
          importantPlaces={importantPlaces}
        />
      )}
    </WhiteModal>
  );
};

export default ImportantPlacesModal;
